import { api } from "@awtoban/shared";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "../../lib/useDebouncedValue";

export interface VehicleHistoryHit {
  plate: string;
  owner_name: string;
  default_total_axles: number | null;
  last_seen_at: string;
  last_transaction: {
    total_axles: number;
    front_axle_tn: string;
    is_oversized: boolean;
    requires_permit: boolean;
  } | null;
}

/** Returns the most recent vehicle record matching `plate` (>=2 chars), debounced. */
export function useVehicleHistory(plate: string): VehicleHistoryHit | null {
  const debounced = useDebouncedValue(plate.trim(), 200);
  const enabled = debounced.length >= 2;

  const q = useQuery({
    queryKey: ["vehicle-lookup", debounced],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const result = await api.GET("/api/v1/vehicles/", {
        params: { query: { plate: debounced } },
      } as never);
      const data = (result.data as VehicleHistoryHit[] | undefined) ?? [];
      // Server returns most-recent first; pick exact match if any, otherwise first hit.
      const exact = data.find((d) => d.plate === debounced.toUpperCase());
      return exact ?? data[0] ?? null;
    },
  });
  return q.data ?? null;
}
